import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import {
  evaluateConditions,
  evaluateConditionsForPreview,
  isDisabledByRule,
  getInstanceCount,
  mirrorSourceOf,
  qualifiedKey,
  removeInstance,
  renderInstanceLabel,
  scopeValuesToInstance,
  sectionMinInstances,
  setInstanceCount,
  withheldFieldIds,
  type StepAudience,
} from '@bmo/form-core';
import { Button } from 'antd';
import { type ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import {
  isLayoutBlockType,
  type FormSection,
  type WorkflowField,
  type WorkflowStep,
} from '@/api/workflows';

import { ClientLanguageContext } from './clientLanguage';
import { applySectionInstanceDefaults } from './defaultOptions';
import { FieldRenderer, type FieldValue } from './FieldRenderer';
import { FreshEntryErrorScope } from './FreshEntryErrorScope';
import { hiddenBorrowedFieldIds } from './hiddenReveals';

type Props = {
  step: WorkflowStep;
  values: Record<string, FieldValue>;
  onChange: (fieldId: string, next: FieldValue) => void;
  language: 'en' | 'fr';
  mode: 'preview' | 'advisor';
  /**
   * Who this runtime serves. Separate from `mode` and NOT derivable from it:
   * `mode` is narrowed to two members before it reaches here precisely so
   * `'public'` cannot leak into the leaves (see formShellMode.ts), and a public
   * customer maps to `'advisor'` for everything the field tree normally cares
   * about. This is the one question where they differ — a public customer has no
   * session, so an advisor-sourced field is withheld rather than rendered empty.
   *
   * Defaults to `'internal'`, which withholds nothing: every existing call site
   * (advisor runtime, originator/approver preview, the whole test suite) keeps
   * rendering exactly what it renders today.
   */
  audience?: StepAudience;
  /** Renders every field inert — a locked application (BMO owns it now) or the spec's `mode=r`.
   *  Independent of `mode`, which is about WHOSE runtime this is, not whether it accepts input.
   *  Merges with the per-field `field.readOnly` flag at the single disable point in
   *  FieldRenderer. */
  readOnly?: boolean;
  /** Whether address fields may call the Canada Post proxy. Defaults to on; the PUBLIC
   *  customer runtime passes false. Three reasons compound there: every Find is a paid
   *  Canada Post transaction fired on KEYSTROKE, the proxy is RequireFormRuntime-gated so an
   *  anonymous caller would get a dead dropdown anyway, and PDF generation is the only
   *  request that runtime is allowed to make. Fields fall through to the plain Input, which
   *  is fully functional - the customer types the address. */
  addressAutocomplete?: boolean;
  /** Cross-field uniqueness lookup, threaded down to each ShortText
   *  field's on-blur check. Returns the peer's advisor-facing label when
   *  the value collides with a peer in the same `uniquenessGroup`, or
   *  null when there's no conflict. Owned by FormShell so the walk sees
   *  every step's values. */
  findUniquenessConflictMessage?: (
    field: WorkflowField,
    value: FieldValue,
    ownValueKey?: string,
  ) => string | null;
  /** Batch update the values map — used by Repeatable Section
   *  Add/Remove which need to touch many keys atomically (shift qualified
   *  keys when removing an instance, bump the sentinel count when
   *  adding). Regular single-field edits still flow through `onChange`. */
  mutateValues?: (
    updater: (prev: Record<string, FieldValue>) => Record<string, FieldValue>,
  ) => void;
  /** Every section of the workflow. Lets a "repeat once per instance of" section find the
   *  section it mirrors (it then renders no Add / Remove of its own), lets removing an instance
   *  take the mirrors' matching instance with it, and lets per-instance conditions reach across
   *  that link. Optional: without it every section behaves as an ordinary repeatable. */
  allSections?: readonly FormSection[];
};

/** Layout the originator authored for a borrowed field — used by the
 *  RadioGroup / CheckboxGroup cases to reproduce the field's column-grid
 *  context inside the reveal slot (so a half-width input that was authored
 *  in a 2-col row stays half-width when revealed). */
export type BorrowedLayout = {
  /** Number of columns in the row the borrowed field was placed in. */
  columnCount: number;
  /** Zero-based index of the borrowed field within its row. */
  columnIndex: number;
};

/** AddressComplete sibling-fill roles. Stamped on each field of the
 *  Address compound blueprint (or set by the originator on a hand-built
 *  field via the properties panel). The renderer matches by role within
 *  the trigger field's section so multiple Address compounds on one step
 *  don't cross-fill, and renaming a label never breaks the sibling
 *  population. Kept in lockstep with the BE allow-list. */
export type AddressRole = 'street' | 'city' | 'province' | 'postal' | 'apt' | 'full' | 'aptStreet';

/**
 * Walk every field on the step into indexed maps:
 *   - `byId`     : fieldId → field (powers FieldRenderer's inline-reveal lookup)
 *   - `borrowed` : fieldId of every field that's borrowed by a RadioGroup /
 *                  CheckboxGroup option's `revealFieldId`. Borrowed fields are
 *                  skipped by the section walker so they don't double-render —
 *                  they only appear in their parent option's inline slot.
 *   - `borrowedSections` : sectionId of every section borrowed WHOLE by an
 *                  option's `revealSectionId`. Same rule one level up: the
 *                  section is skipped by the top-level walker and renders only
 *                  inside its owning option's card.
 *   - `layout`   : fieldId → BorrowedLayout. Lets those cases reproduce the
 *                  authored row width constraint inside the reveal slot.
 *   - `rolesByField` : fieldId → (role → field), for every address-role field.
 *                      Powers the AddressComplete sibling-fill. Scoped to the
 *                      field's own ADDRESS BLOCK, not to the whole section:
 *                      a section can legitimately hold several address blocks
 *                      (RESP's beneficiary step has four — the locked and the
 *                      editable beneficiary address, plus the individual and
 *                      entity primary-caregiver addresses) and picking a
 *                      sibling would otherwise always land on the first one.
 *                      A block is a run of address-role fields in document
 *                      order; a repeat of a role already in the run closes it
 *                      and opens the next. Non-address fields between them
 *                      don't split a block, so a street row followed by a
 *                      city / province / postal row still resolves as one.
 */
// Exported for tests — address-block grouping is easy to regress and hard to
// reach through a full render.
export function indexStepFields(step: WorkflowStep): {
  byId: Map<string, WorkflowField>;
  borrowed: Set<string>;
  borrowedSections: Set<string>;
  layout: Map<string, BorrowedLayout>;
  rolesByField: Map<string, Map<AddressRole, WorkflowField>>;
} {
  const byId = new Map<string, WorkflowField>();
  const borrowed = new Set<string>();
  const borrowedSections = new Set<string>();
  const layout = new Map<string, BorrowedLayout>();
  const rolesByField = new Map<string, Map<AddressRole, WorkflowField>>();
  for (const section of step.sections) {
    // Fields of the block currently being accumulated, and the shared role
    // map they will all point at once the block closes.
    let blockRoles = new Map<AddressRole, WorkflowField>();
    let blockMembers: string[] = [];
    const closeBlock = () => {
      for (const id of blockMembers) rolesByField.set(id, blockRoles);
      blockRoles = new Map<AddressRole, WorkflowField>();
      blockMembers = [];
    };
    for (const row of section.rows) {
      row.columns.forEach((col, columnIndex) => {
        const f = col.field;
        if (!f) return;
        byId.set(f.id, f);
        layout.set(f.id, { columnCount: row.columns.length, columnIndex });
        if (f.addressRole) {
          if (blockRoles.has(f.addressRole)) closeBlock();
          blockRoles.set(f.addressRole, f);
          blockMembers.push(f.id);
        }
        if (f.type === 'CheckboxGroup' || f.type === 'RadioGroup') {
          for (const o of f.options) {
            if (o.revealFieldId) borrowed.add(o.revealFieldId);
            if (o.revealSectionId) borrowedSections.add(o.revealSectionId);
          }
        }
      });
    }
    closeBlock();
  }
  return { byId, borrowed, borrowedSections, layout, rolesByField };
}

/**
 * Walks a step's section → row → column tree and renders each placed field
 * via {@link FieldRenderer}. Section title renders above each group as the
 * page heading; any introductory copy / disclaimers / "all fields mandatory"
 * notes are now authored by the originator as Static Text / Paragraph blocks
 * (with configurable font size + color) — nothing is hardcoded into the
 * runtime renderer anymore.
 *
 * Stateless beyond the values map — the caller owns the values object so the
 * shell can compute per-step completion + persist across step navigation in
 * Preview, and so the eventual Advisor route can save to the BE on change.
 */
export function FormRenderer({
  step,
  values,
  onChange,
  language,
  mode,
  audience = 'internal',
  readOnly,
  addressAutocomplete,
  findUniquenessConflictMessage,
  mutateValues,
  allSections,
}: Props) {
  // Precompute borrowed-field set + id index + layout map once per render so
  // SectionBlock can skip borrowed fields cheaply and FieldRenderer (in the
  // RadioGroup / CheckboxGroup cases) can resolve `revealFieldId` + replay the
  // authored row column-grid for the borrowed field.
  const { byId, borrowed, borrowedSections, layout, rolesByField } = indexStepFields(step);
  // Advisor-sourced fields this audience must not see — empty (and free) for every
  // internal audience. The SAME set FormShell hands its completeness walkers, so a
  // field withheld here cannot still be holding Next back.
  const withheld = withheldFieldIds(step, audience);
  // Withholding at the LOOKUP, not only in the section walk below. An advisor-sourced
  // field can also be borrowed by a Radio/Checkbox option's `revealFieldId`, in which
  // case it renders from inside that option's card and never passes through the walk at
  // all. Resolving it to undefined is what closes that second door — FieldRenderer
  // already treats an unresolvable reveal target as "nothing to reveal".
  const getFieldById = (id: string) => (withheld.has(id) ? undefined : byId.get(id));
  // Fields borrowed by an option that is NOT picked render nowhere; their stale answers must not
  // feed another field's rule (QA D-17: the funds total kept the hidden cheque amount as target).
  const hiddenBorrowed = hiddenBorrowedFieldIds(step, values);
  const getBorrowedLayout = (id: string) => layout.get(id);
  // Sibling-fill finder, bound to ONE trigger field's address block. Handed
  // down per-column so a step with several address blocks fills the block the
  // advisor is actually typing in.
  const roleFinderFor = (fieldId: string) => (role: AddressRole) =>
    rolesByField.get(fieldId)?.get(role);
  // Note: getValueById / setValueById are curried per-instance inside
  // SectionBlock so sibling-fill (AddressComplete, option-group
  // inline-reveal) resolves the right qualified key inside a
  // repeatable section. Non-repeatable sections curry to the bare id
  // — nothing changes for that path.

  // A section borrowed by an option (`revealSectionId`) is lifted out of normal
  // flow exactly like a borrowed FIELD is, and renders only inside its owning
  // option's card. Filtered BEFORE the map rather than skipped inside it so the
  // continuation check below compares against the next section that actually
  // renders — skipping in-place would let a borrowed section sit between two
  // siblings and silently break their spacing.
  const flowSections = step.sections.filter((s) => !borrowedSections.has(s.id));

  /**
   * Render a borrowed section inside an option's reveal slot.
   *
   * Passed DOWN as a closure rather than having FieldRenderer import
   * SectionBlock: FormRenderer already imports FieldRenderer, so the direct call
   * would close an import cycle. Same prop-injection shape as `getFieldById` /
   * `getBorrowedLayout` — callers that don't supply it fall back to rendering
   * nothing, which is what every non-runtime consumer wants anyway.
   *
   * Guarded on `borrowedSections` so a stale id, or an id naming a section that
   * still renders in normal flow, resolves to null instead of double-rendering
   * the block in two places at once.
   */
  const renderRevealSection = (sectionId: string, inheritedDisabled?: boolean) => {
    if (!borrowedSections.has(sectionId)) return null;
    const target = step.sections.find((s) => s.id === sectionId);
    if (!target) return null;
    return (
      <SectionBlock
        section={target}
        stepId={step.id}
        values={values}
        onChange={onChange}
        language={language}
        mode={mode}
        // Most-restrictive wins: a section revealed by a disabled option inherits that
        // state and can never re-enable itself from inside.
        readOnly={readOnly || inheritedDisabled}
        addressAutocomplete={addressAutocomplete}
        borrowedFieldIds={borrowed}
        withheldFields={withheld}
        getFieldById={getFieldById}
        hiddenBorrowed={hiddenBorrowed}
        getBorrowedLayout={getBorrowedLayout}
        roleFinderFor={roleFinderFor}
        findUniquenessConflictMessage={findUniquenessConflictMessage}
        mutateValues={mutateValues}
        renderRevealSection={renderRevealSection}
        allSections={allSections}
        revealSlot
      />
    );
  };

  return (
    <div>
      {flowSections.map((section, i) => {
        // An untitled section is a CONTINUATION of the one above it (used when
        // a single visual block needs to span two sections — e.g. the
        // Accountholder page keeps its language radio and personal fields in
        // separate sections so row indices stay stable, but the design shows
        // one heading over both). Without this the previous section's 28-px
        // separator opens a gap mid-block that reads as a section break.
        const next = flowSections[i + 1];
        const nextIsContinuation = !!next && !next.titleEn && !next.titleFr;
        return (
          <SectionBlock
            key={section.id}
            section={section}
            stepId={step.id}
            values={values}
            onChange={onChange}
            language={language}
            mode={mode}
            readOnly={readOnly}
            addressAutocomplete={addressAutocomplete}
            borrowedFieldIds={borrowed}
            withheldFields={withheld}
            getFieldById={getFieldById}
            hiddenBorrowed={hiddenBorrowed}
            getBorrowedLayout={getBorrowedLayout}
            roleFinderFor={roleFinderFor}
            findUniquenessConflictMessage={findUniquenessConflictMessage}
            mutateValues={mutateValues}
            renderRevealSection={renderRevealSection}
            allSections={allSections}
            tightBottom={nextIsContinuation}
          />
        );
      })}
    </div>
  );
}

function SectionBlock({
  section,
  stepId,
  values,
  onChange,
  language,
  mode,
  readOnly,
  addressAutocomplete,
  borrowedFieldIds,
  withheldFields,
  getFieldById,
  hiddenBorrowed,
  getBorrowedLayout,
  roleFinderFor,
  findUniquenessConflictMessage,
  mutateValues,
  renderRevealSection,
  allSections,
  revealSlot,
  tightBottom,
}: {
  section: FormSection;
  /** Owning step's id — used by the preview-mode section-visibility
   *  branch to distinguish same-step reveals (still hide) from
   *  cross-step branch gating (bypass for the "no hidden things in
   *  preview" rule). */
  stepId: string;
  values: Record<string, FieldValue>;
  onChange: (fieldId: string, next: FieldValue) => void;
  language: 'en' | 'fr';
  mode: 'preview' | 'advisor';
  /** See {@link Props.readOnly}. */
  readOnly?: boolean;
  /** See {@link Props.addressAutocomplete}. */
  addressAutocomplete?: boolean;
  borrowedFieldIds: Set<string>;
  /** Fields this audience must not see (see {@link Props.audience}). Checked beside
   *  `borrowedFieldIds` at every point that one is — same shape, same three sites, so
   *  neither can be honoured without the other. */
  withheldFields: ReadonlySet<string>;
  getFieldById: (id: string) => WorkflowField | undefined;
  /** Borrowed fields whose revealing option is not picked — read as blank by sibling rules. */
  hiddenBorrowed: Set<string>;
  getBorrowedLayout: (id: string) => BorrowedLayout | undefined;
  roleFinderFor: (fieldId: string) => (role: AddressRole) => WorkflowField | undefined;
  findUniquenessConflictMessage?: (
    field: WorkflowField,
    value: FieldValue,
    ownValueKey?: string,
  ) => string | null;
  mutateValues?: (
    updater: (prev: Record<string, FieldValue>) => Record<string, FieldValue>,
  ) => void;
  /** Renders a section borrowed by an option's `revealSectionId`. Threaded
   *  through to FieldRenderer, which owns the option cards the block lands in.
   *  `inheritedDisabled` carries the owning control's disable state into the block. */
  renderRevealSection?: (sectionId: string, inheritedDisabled?: boolean) => ReactNode;
  /** See {@link Props.allSections}. */
  allSections?: readonly FormSection[];
  /** This block IS an option's reveal — it is rendering INSIDE a card rather
   *  than in the page flow. Two things change: its own conditions are bypassed
   *  (visibility belongs to the owning option, exactly as for a borrowed field),
   *  and the title drops from a 24-px page heading to an in-card sub-heading. */
  revealSlot?: boolean;
  /** Collapse the bottom separator because the NEXT section is an untitled
   *  continuation of this one — the two render as a single visual block. */
  tightBottom?: boolean;
}) {
  const { t } = useTranslation();
  // A new instance starts from its "Selected by default" answers like the first one did — it
  // has never been shown, so there is nothing of the advisor's to protect. The client's
  // language decides whether a language-scoped default is offered (null outside a shell, which
  // offers every option — the same rule the option controls follow).
  const clientLanguage = useContext(ClientLanguageContext);
  const sectionTitle = language === 'fr' && section.titleFr ? section.titleFr : section.titleEn;

  // Add-button copy. The originator already names each instance ("Account
  // Holder {n}"), so the noun is reused here instead of adding a second
  // property they'd have to keep in sync. Strip the {n} placeholder and any
  // separator it leaves behind; fall back to the generic label when unset.
  const rawInstanceLabel =
    (language === 'fr' ? section.instanceLabelFr : section.instanceLabelEn) ??
    section.instanceLabelEn ??
    '';
  const instanceNoun = rawInstanceLabel
    .replace(/\{n\}/g, '')
    .replace(/[\s\-–—:]+$/, '')
    .trim();

  // Visibility gate at the section level.
  //
  //   Advisor mode         → a hidden section vanishes entirely so the
  //                          flow reads exactly as authored.
  //   Originator preview   → mixed:
  //     - A condition that references a field on a PRIOR step (Beneficiary
  //       Type gating Eligibility Requirements, etc.) is bypassed ONLY
  //       while that upstream field is still unanswered — the tab must
  //       never present as a blank page just because nothing was chosen
  //       upstream. Once the originator DOES answer it, the branch is
  //       honored (a section gated `is_not RESP` disappears once RESP is
  //       picked). That's the "no hidden things in preview" rule, scoped
  //       to unanswered upstream.
  //     - Conditions that reference fields on the SAME step always apply.
  //       Those are the intentional-reveal patterns (a CheckboxGroup that
  //       reveals an inline sub-section right below it); showing every
  //       reveal at once defeats the point.
  //
  // A reveal slot skips the gate entirely. Its visibility is the owning
  // option's checked state — FieldRenderer only calls the closure for a TICKED
  // option — and a borrowed section carries no conditions of its own, exactly
  // like a borrowed field. Evaluating anyway would also break preview mode,
  // where `evaluateConditionsForPreview` deliberately bypasses same-step gates:
  // the block would render both inside the card AND nowhere else, or vanish
  // from a card the advisor had just ticked.
  const visible =
    revealSlot ||
    (mode === 'preview'
      ? evaluateConditionsForPreview(
          section.conditions,
          section.conditionCombinator,
          values,
          stepId,
        )
      : evaluateConditions(section.conditions, section.conditionCombinator, values));
  if (!visible) return null;

  // "Disable if" on the SECTION. Evaluated against the bare `values`, matching how
  // `section.conditions` is evaluated right above — so every instance of a repeatable
  // section disables together (a FIELD rule, below, is per-instance). Note this is the
  // INVERSE polarity of the visibility rule: a MATCH disables. `isDisabledByRule` is the
  // only place that inversion lives — see its doc comment for why writing
  // `!evaluateConditions(...)` here would be wrong.
  //
  // Most-restrictive wins: an ancestor's disable is OR'd in and can never be undone here.
  const sectionDisabled = readOnly === true || isDisabledByRule(section, values);

  // Find the first VISIBLE field in this section. When it's a text-block
  // (StaticText / Paragraph) the field's own 20-px marginTop already
  // provides spacing under the section title — adding another 18-px gap
  // from the h3 would double up. We zero the h3's bottom margin in that
  // case so the gap matches non-text-block sections (where the h3's 18-px
  // bottom IS the gap).
  let firstVisibleFieldType: WorkflowField['type'] | null = null;
  for (const row of section.rows) {
    for (const col of row.columns) {
      const f = col.field;
      if (!f) continue;
      // Borrowed fields don't render in normal flow — skip them for the
      // first-visible-type heuristic too, otherwise the section heading
      // margin would react to a field the advisor never sees here.
      if (borrowedFieldIds.has(f.id)) continue;
      // Same reasoning for a field this audience never sees: the heading gap
      // must follow the first block the CUSTOMER actually reads.
      if (withheldFields.has(f.id)) continue;
      if (!evaluateConditions(f.conditions, f.conditionCombinator, values)) continue;
      firstVisibleFieldType = f.type;
      break;
    }
    if (firstVisibleFieldType) break;
  }
  // Any layout block (StaticText / Paragraph / Notice / BulletedList /
  // Divider / Image) carries its own top margin (DEFAULT_TEXT_MARGIN_PX
  // or an authored override), so collapsing the h3's bottom margin stops
  // the two margins from stacking into a ~40 px void between the
  // section title and the first callout. Non-layout fields (inputs)
  // keep the 18 px bottom margin so the label reads with breathing room.
  const firstIsSpacedBlock =
    firstVisibleFieldType !== null && isLayoutBlockType(firstVisibleFieldType);

  // §1 Repeatable Sections. When `section.repeatable` is true, render
  // one copy of the row/column tree per instance and expose Add / Remove
  // affordances. Non-repeatable sections render exactly one instance
  // with the identity key mapping — behaviour is unchanged for them.
  const repeatable = section.repeatable === true;
  // Advisor-side floor: seed this many starter instances and refuse to remove
  // below it. Defaults to 1 (unchanged behaviour) when unset; 0 means the
  // block is optional and starts empty.
  const minInstances = sectionMinInstances(section);
  const instanceCount = repeatable ? getInstanceCount(values, section.id, minInstances) : 1;
  const maxInstances = section.maxInstances ?? null;
  // A "repeat once per instance of" section takes its count from the section it mirrors
  // (FormShell keeps the two in step), so it offers neither Add nor Remove of its own.
  const mirrored = allSections !== undefined && mirrorSourceOf(section, allSections) !== null;
  const canAddMore =
    repeatable &&
    !mirrored &&
    // A disabled section offers no Add — spawning a fresh instance is an edit, and the
    // section is inert. Mirrors how an absent `mutateValues` withdraws it under the shell lock.
    !sectionDisabled &&
    (maxInstances === null || instanceCount < maxInstances) &&
    // Opt-in gate on "Add another" (RESP: another beneficiary only for a Family Plan).
    evaluateConditions(section.addConditions ?? [], section.addConditionCombinator, values);

  // "Add another X" is wrong when there is no X yet — an optional block
  // (minInstances 0) starts empty, so the first add reads "Add X".
  const isFirstInstance = repeatable && instanceCount === 0;
  const addInstanceLabel = instanceNoun
    ? t(
        isFirstInstance
          ? 'formRenderer.repeatable.addFirstInstanceNamed'
          : 'formRenderer.repeatable.addInstanceNamed',
        { name: instanceNoun },
      )
    : t(
        isFirstInstance
          ? 'formRenderer.repeatable.addFirstInstance'
          : 'formRenderer.repeatable.addInstance',
      );

  const renderInstance = (instanceIdx: number) => {
    const keyOf = (fieldId: string) =>
      repeatable ? qualifiedKey(section.id, instanceIdx, fieldId) : fieldId;
    // Instance-scoped versions of get/setValueById so downstream sibling
    // lookups (AddressComplete auto-fill of City / Province / Postal,
    // CheckboxGroup inline reveal, etc.) read and write the CURRENT
    // instance's qualified key when the section is repeatable. Non-
    // repeatable sections fall through to the bare field id via keyOf,
    // so nothing regresses for the non-repeatable path.
    const scopedGetValueById = (id: string): FieldValue =>
      hiddenBorrowed.has(id) ? undefined : values[keyOf(id)];
    const scopedSetValueById = (id: string, next: FieldValue): void => onChange(keyOf(id), next);
    // Field-level conditions inside a repeatable section evaluate against
    // THIS instance's answers: a "Custodial Parent: Other" radio on
    // beneficiary 2 must reveal beneficiary 2's name fields and leave
    // beneficiary 1 alone. Conditions pointing at fields outside the
    // section (a page-level question above it) still resolve normally —
    // scopeValuesToInstance only rebinds ids authored in this section.
    const condValues = scopeValuesToInstance(section, values, instanceIdx, allSections);
    const body = (
      <div
        key={`instance-${instanceIdx}`}
        // No card chrome: the 2026-07-24 BMO design renders each instance as
        // plain content, identical to a non-repeatable section. The instance
        // label ("Accountholder 1" / "Accountholder 2") carries the grouping,
        // so the old tinted-and-bordered box only made repeated fields sit on
        // a different background and indent out of line with the rest of the
        // page. Bottom margin is the only separation between instances.
        style={{ marginBottom: repeatable && instanceCount > 1 ? 20 : 0 }}
      >
        {repeatable && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 12,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
              {renderInstanceLabel(section, instanceIdx, language)}
            </div>
            {/* Only an ADDED instance can be removed (QA D-26 / AH-033). The first `minInstances`
                are the ones the form requires, so Accountholder 1 never offers Remove — even
                once Accountholder 2 exists and the count alone would allow one to go. */}
            {!mirrored &&
              // Removing an instance is an edit too — withdrawn while the section is disabled.
              !sectionDisabled &&
              instanceIdx >= minInstances &&
              instanceCount > minInstances &&
              mutateValues && (
                <Button
                  danger
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    mutateValues((prev) => removeInstance(prev, section, instanceIdx, allSections))
                  }
                >
                  {t('formRenderer.repeatable.removeInstance')}
                </Button>
              )}
          </div>
        )}
        {section.rows.map((row) => {
          const rowHasVisibleField = row.columns.some((col) => {
            if (!col.field) return false;
            if (borrowedFieldIds.has(col.field.id)) return false;
            // A row of nothing but advisor-sourced fields collapses entirely for a
            // public customer rather than leaving an empty grid cell behind.
            if (withheldFields.has(col.field.id)) return false;
            return evaluateConditions(
              col.field.conditions,
              col.field.conditionCombinator,
              condValues,
            );
          });
          if (!rowHasVisibleField) return null;
          return (
            <div
              key={row.id}
              // `bmo-form-row` + `data-cols` are what the narrow-shell collapse in
              // global.css keys off — two columns on a tablet-width shell, one on a
              // phone. The authored count stays in the inline template because it is
              // DATA; the CSS only caps it (see the `!important` note there).
              className="bmo-form-row"
              data-cols={row.columns.length}
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${row.columns.length}, minmax(0, 1fr))`,
                gap: 16,
                marginBottom: 4,
              }}
            >
              {row.columns.map((col) => {
                // A column with nothing to show still holds its grid cell at full
                // width, so the fields beside it keep the alignment they were
                // authored with. Once the row collapses that alignment is gone
                // anyway, and the class lets the CSS drop these — otherwise each
                // empty cell becomes an empty ROW carrying the 16px gap, and a
                // conditionally-hidden field leaves a visible hole on a phone.
                const placeholder = <div key={col.id} className="bmo-form-row__empty" />;
                if (!col.field) return placeholder;
                if (borrowedFieldIds.has(col.field.id)) return placeholder;
                if (withheldFields.has(col.field.id)) return placeholder;
                if (
                  !evaluateConditions(
                    col.field.conditions,
                    col.field.conditionCombinator,
                    condValues,
                  )
                ) {
                  return placeholder;
                }
                const key = keyOf(col.field.id);
                // "Disable if" on the FIELD. Against `condValues`, not `values` — the same
                // instance-scoped map its visibility rule just used — so instance 2 of a
                // repeatable section can be disabled while instance 1 stays editable.
                // OR'd with the section's, so most-restrictive wins: a field can never
                // re-enable itself against a disabled section or step.
                const fieldDisabled = sectionDisabled || isDisabledByRule(col.field, condValues);
                return (
                  <div key={col.id}>
                    <FieldRenderer
                      field={col.field}
                      value={values[key]}
                      onChange={(next) => onChange(key, next)}
                      language={language}
                      mode={mode}
                      readOnly={fieldDisabled}
                      addressAutocomplete={addressAutocomplete}
                      getFieldById={getFieldById}
                      getValueById={scopedGetValueById}
                      setValueById={scopedSetValueById}
                      getBorrowedLayout={getBorrowedLayout}
                      findFieldByRole={roleFinderFor(col.field.id)}
                      renderRevealSection={renderRevealSection}
                      findUniquenessConflictMessage={findUniquenessConflictMessage}
                      // Instance-qualified key so the uniqueness check skips
                      // THIS instance, not always instance 0 — fixes the
                      // repeatable-section self-collision false positive.
                      valueKey={key}
                      // Cross-field rules (a Date's mustFollowFieldId) resolve
                      // other fields exactly as this instance's conditions do.
                      getConditionValue={(id) => condValues[id]}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
    // An instance the advisor adds AFTER a refused Next starts without red — see
    // FreshEntryErrorScope. Only repeatable sections take the scope: a non-repeatable
    // section's single instance mounts with the step, so it has nothing to opt out of.
    return repeatable ? (
      <FreshEntryErrorScope key={`instance-${instanceIdx}`}>{body}</FreshEntryErrorScope>
    ) : (
      body
    );
  };

  return (
    <section
      style={{
        // A reveal slot is already inside the card's padding and is followed by
        // the next option card, so the 28-px page separator would just add dead
        // space at the bottom of the card.
        marginBottom: revealSlot ? 0 : tightBottom ? 4 : 28,
      }}
    >
      {sectionTitle && instanceCount > 0 && revealSlot && (
        // In-card sub-heading ("Information about the relinquishing issuer" in
        // the BMO transfer design) — the block sits UNDER an option label, so a
        // 24-px page heading would out-shout the question it belongs to.
        <h3
          style={{
            fontSize: 16,
            fontWeight: 700,
            margin: firstIsSpacedBlock ? '4px 0 0 0' : '4px 0 16px 0',
          }}
        >
          {sectionTitle}
        </h3>
      )}
      {sectionTitle && instanceCount > 0 && !revealSlot && (
        // Section title plays the role of the page heading. Bottom margin
        // gives the first row breathing room — except when the first row is
        // a text block that already carries its own marginTop.
        //
        // Suppressed while an optional block (minInstances 0) sits empty: a
        // heading with nothing under it but an Add link reads as a broken
        // section. The heading appears with the first instance, matching the
        // live "+ Add Joint Subscriber" pattern.
        <h2
          style={{
            fontSize: 24,
            fontWeight: 700,
            margin: firstIsSpacedBlock ? 0 : '0 0 16px 0',
          }}
        >
          {sectionTitle}
        </h2>
      )}
      {Array.from({ length: instanceCount }).map((_, i) => renderInstance(i))}
      {canAddMore && mutateValues && (
        <Button
          type="link"
          icon={<PlusCircleOutlined style={{ fontSize: 18 }} />}
          onClick={() =>
            mutateValues((prev) =>
              applySectionInstanceDefaults(
                section,
                instanceCount,
                setInstanceCount(prev, section.id, instanceCount + 1),
                clientLanguage,
              ),
            )
          }
          // 2026-07-24 BMO design: a blue uppercase text action with a
          // plus-in-circle, not AntD's dashed placeholder button.
          // `.bmo-add-another` lets the originator-authored noun wrap to a
          // second line instead of running off a phone's edge.
          className="bmo-add-another"
          style={{
            marginTop: 8,
            paddingLeft: 0,
            height: 'auto',
            color: '#0079C1',
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >
          {addInstanceLabel}
        </Button>
      )}
    </section>
  );
}
